import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';

import CheckboxInputListFilter, { Option, InputOption } from '../../../components/Filters/CheckboxInputListFilter';
import { FilterProps } from './FilterProps';
import { SalesLocation } from '../../../types/customers';
import styled from 'styled-components/macro';
import { colors } from '../../../styles/constants';
import { F } from 'lodash/fp';
import { optionValues } from '../../../customers/customerDetails/addresses/components/AddressTypeDropdown';
import { usePreviousProps } from '@mui/utils';

export interface StoreIdFilterProps extends FilterProps {
    salesLocations: SalesLocation[];
}

const toLabel = (salesLocation: SalesLocationWithId) => {
    const { name, location_id } = salesLocation;
    if (!name) {
        return location_id;
    }
    return (
        <>
            <span>{name}</span>
            <OptionSublabel>{location_id}</OptionSublabel>
        </>
    );
};

type SalesLocationWithId = SalesLocation & Required<Pick<SalesLocation, 'location_id'>>;

const StoreIdFilter = (
    {
        queryParams,
        t,
        onChange,
        salesLocations,
    }: StoreIdFilterProps
) => {
    const options: Option[] = (salesLocations || [])
        .filter((location): location is SalesLocationWithId => location.location_id !== undefined)
        .map((location) => ({
            label: toLabel(location),
            value: location.location_id,
        }));
    const optionsValues = options.map(o => o.value);
    const prevOptionsValues = usePreviousProps(optionsValues);

    const values = queryParams.getAll('store_id');
    const initialCustomValue = values.find(v => {
        const result = !optionsValues.includes(v);
        return result;
    })?.trim();
    const [customValue, setCustomValue] = useState(initialCustomValue || '');
    useEffect(() => {
        // handle case where component is mounted with a filter value before salesLocations are loaded
        // leading to a false positive customValue
        if (JSON.stringify(prevOptionsValues) !== JSON.stringify(optionsValues)) {
            const updatedCustomValue = values.find(v => {
                const result = !optionsValues.includes(v);
                return result;
            })?.trim();
            setCustomValue(updatedCustomValue || '');

        }
    }, [optionsValues, values, prevOptionsValues]);

    const customInput: InputOption = {
        label: t('payments.filter.store_id.custom_input_label'),
        value: customValue,
        placeholder: '',
        disabled: false,
        onChange: (value) => setCustomValue(value.trim()),
        onBlur: (value) => setCustomValue(value.trim()),
    };

    return (
        <CheckboxInputListFilter
            label={t('payments.filter.store_id.label', {
                count: values.length,
                value: values.join(', '),
            })}
            title={t('payments.filter.store_id.title')}
            name="store_id"
            values={values}
            options={options}
            onApply={(name, values) => {
                onChange({ [name]: [customValue, ...values].filter(x => x.trim()) });
            }}
            onDelete={(name) => {
                onChange({ [name]: []});
            }}
            inputOptions={[customInput]}
        />
    );
};

const OptionSublabel = styled.div`
    font-size: 0.8em;
    color: ${colors.textSecondary};
`;

export default withTranslation()(StoreIdFilter);
